import { Backdrop, Box, Portal, useTheme } from "@mui/material";
import YantraSingleCard from "./singleCard";
import { addActiveCard, resetBets, updateYantrasRows } from "../../stores/cardsStore/CardsStore";
import { useCardstore } from "../../stores/cardsStore/useCardsStore";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useStatusStore } from "../../stores/statusStore/useStatusStore";
import { Yantra, YantraExecutionInfo } from "../../tanstack/queries/player";
import { useSpring, animated } from "@react-spring/web";
import { getRandomInt } from "../../utils/utils";
import SelectMp3 from "../../assets/music/select.mp3";
import WinMp3 from "../../assets/music/win.mp3";
import DrumRollMp3 from "../../assets/music/drumRoll.mp3";
import { useWebSocket } from "../../hooks/useWebSocket";
import { useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import { useTranslate } from "../../hooks/useTranslate";
import WinnerNotification from "./WinnerNotification";
import { useSoundStore } from "../../stores/soundStore/useSoundStore";
import { getActiveExecutionId } from "../../stores/statusStore/statusStore";

const AnimatedBox = animated(Box);
const SelectAudio = new Audio(SelectMp3);
const WinAudio = new Audio(WinMp3);
const DrumRollAudio = new Audio(DrumRollMp3);

function YantraCards({ yantras, rows, cols }: { yantras: Array<Yantra>; rows: number; cols: number }) {
  const { yantrasColumns, cardIds, requestBody } = useCardstore();
  const { isDisabled, status, activeExecutionId } = useStatusStore();
  const queryClient = useQueryClient();
  const { t } = useTranslate();
  const theme = useTheme();
  const { isMuted } = useSoundStore();

  useEffect(() => {
    if (yantras) {
      updateYantrasRows(yantras, rows, cols);
    }
  }, [yantras]);

  const checkCardActive = (id: string) => !!cardIds?.[id];

  const [activeIndex, setActiveIndex] = useState<string | null>(null);
  const [isWinning, setIsWinning] = useState<boolean>(false);

  const animationStyles = useSpring({
    scale: activeIndex !== null ? 1.1 : 1,
    boxShadow: "0px 0px 10px 3px #bb7954",
    config: { tension: 300, friction: 15 },
  });

  const [winningIndex, setWinningIndex] = useState<null | string>(null);
  const [userPayout, setUserPayout] = useState(0);

  useEffect(() => {
    if (!winningIndex || isWinning) return () => {};
    let interval: number;
    const usedIndices = new Set<number>();
    let visitedAmount = Math.min(getRandomInt(Math.ceil(0.6 * yantras.length), Math.ceil(1.2 * yantras.length)), 10);

    const getRandomIndex = (): number => {
      let randomIndex: number;
      do {
        randomIndex = Math.floor(Math.random() * yantras.length);
      } while (usedIndices.has(randomIndex));

      // Очистить Set, если все индексы уже были использованы
      if (usedIndices.size >= yantras.length - 1) {
        usedIndices.clear();
      }

      usedIndices.add(randomIndex);
      return randomIndex;
    };

    const startAnimation = () => {
      interval = setInterval(() => {
        setActiveIndex(visitedAmount <= 0 ? winningIndex : yantras[getRandomIndex()].id);
        if (visitedAmount > 0 && !isMuted) SelectAudio.play();
        if (visitedAmount <= 0) {
          clearInterval(interval);
          setActiveIndex(null);
          if (!isMuted) {
            DrumRollAudio.play();
          }
          setTimeout(() => {
            setActiveIndex(winningIndex);
            setIsWinning(true);
            if (!isMuted) {
              WinAudio.play();
            }
            queryClient.invalidateQueries({
              queryKey: ["playerHistory"],
              refetchType: "all",
            });
          }, 2000);
        }
        visitedAmount -= 1;
      }, 800);
    };

    startAnimation();

    return () => clearInterval(interval);
  }, [winningIndex]);

  const highlightWinningCard = useCallback((winCardNumber: string | null) => {
    setWinningIndex(winCardNumber);
  }, []);

  useWebSocket({
    onExecutionUpdate: (executionData: YantraExecutionInfo) => {
      if (executionData.id === getActiveExecutionId() && executionData.status === "CALCULATED") {
        highlightWinningCard(executionData.resultIndex.toString());
      }
    },
    onTicketsUpdate: (executionId: string, totalPayout: number) => {
      if (executionId === getActiveExecutionId()) {
        setUserPayout(0 + totalPayout);
      }
    },
  });

  useEffect(() => {
    if (status === "PLACE_BETS") {
      setUserPayout(0);
      setActiveIndex(null);
      setIsWinning(false);
      setWinningIndex(null);
      resetBets();
    }
  }, [activeExecutionId, status]);

  const isColumnSelected = (index: number) => {
    const key = index.toString();
    return typeof requestBody.rows === "object" && requestBody.rows !== null && Object.prototype.hasOwnProperty.call(requestBody.rows, key);
  };

  const isDisabledCard = (rowIndex: number) => {
    return !isDisabled && !isColumnSelected(rowIndex);
  };

  const isRow = (index: number) => {
    return Boolean(Object.keys(requestBody.rows).length && (requestBody.rows as Record<string, any>)[index.toString()]);
  };

  const winningCard = useMemo(() => {
    if (winningIndex === null || !isWinning) return null;
    for (const column of yantrasColumns) {
      const cardWithin = column.cards.find((card) => card.id === winningIndex);
      if (cardWithin) return cardWithin;
    }
    return null;
  }, [isWinning, winningIndex, yantrasColumns]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        justifyContent: "start",
        alignItems: "center",
        width: "100%",
        height: "fit-content",
      }}
      className="cards-container"
    >
      {winningIndex !== null && (
        <Portal>
          <Backdrop
            open
            sx={{
              zIndex: 10,
              background: isWinning ? "rgba(0,0,0,.7)" : "rgba(0,0,0,.5)",
            }}
          />
          {isWinning && winningCard !== null && <WinnerNotification card={winningCard} payment={userPayout} />}
        </Portal>
      )}
      {!yantrasColumns.length && (
        <Alert variant="filled" severity="error">
          {t("cards.invalid")}
        </Alert>
      )}

      {yantrasColumns.length &&
        yantrasColumns.map((column, index) => (
          <Box
            key={index}
            sx={{
              width: "100%",
              position: "relative",
              display: "flex",
              gap: { xs: 0.5, md: 1 },
              justifyContent: "start",
              alignItems: "center",
              height: "fit-content",
              border: ` ${"6px solid " + column.borderColor}`,
              borderRadius: theme.spacing(2.5),
              p: 2,
              backgroundColor: isColumnSelected(index) ? column.borderColor : "transparent",
            }}
          >
            {isColumnSelected(index) && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  zIndex: 4,
                  borderRadius: 3,
                  overflow: "hidden",
                  pointerEvents: "none",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "200%",
                    height: "100%",
                    background: `linear-gradient(
                    120deg,
                    rgba(255, 215, 0, 0) 0%,
                    rgba(255, 215, 0, 0.4) 50%,
                    rgba(255, 215, 0, 0) 100%
                  )`,
                    animation: "shine 1s infinite",
                  },
                  "@keyframes shine": {
                    "0%": {
                      left: "-100%",
                    },
                    "100%": {
                      left: "100%",
                    },
                  },
                }}
              ></Box>
            )}
            {isRow(index) && (
              <Box
                sx={{
                  width: { xs: theme.spacing(2.5), md: theme.spacing(5) },
                  aspectRatio: "1",
                  bgcolor: "#556bd6",
                  color: "white",
                  fontSize: {
                    xs: theme.typography.pxToRem(12),
                    md: theme.typography.pxToRem(24),
                  },
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  position: "absolute",
                  top: theme.spacing(-1),
                  right: theme.spacing(-1.5),
                  zIndex: 5,
                }}
              >
                {(requestBody.rows as Record<string, any>)[index.toString()]}
              </Box>
            )}
            {column.cards.map((card) => (
              <AnimatedBox
                key={card.id}
                style={{
                  ...(card.id === activeIndex && !isWinning && animationStyles),
                  ...(card.id === activeIndex &&
                    !isWinning && {
                      zIndex: 11,
                    }),
                }}
                sx={{
                  width: "100%",
                  aspectRatio: "17 / 19",
                  borderRadius: 3.25,
                }}
              >
                <YantraSingleCard
                  key={card.id}
                  card={card}
                  isRow={isRow(index)}
                  count={cardIds?.[card.id] || 0}
                  isActive={checkCardActive(card.id)}
                  onClick={(e: React.MouseEvent<HTMLElement>) => (isDisabledCard(index) ? addActiveCard(e, card.id, activeExecutionId) : null)}
                  borderColor={column.borderColor}
                />
              </AnimatedBox>
            ))}
          </Box>
        ))}
    </Box>
  );
}

export default YantraCards;
